/**
 * The Windows policies card on the customer page: the customer's templates and one editor for them.
 *
 *   * THE AGENT ENFORCES. Each PC on a policy changes the settings that do not match (it only
 *     tightens), reads them again and reports. A save reaches every online PC at once, which the card
 *     and the confirmations say.
 *   * EVERY SETTING IS OPT-IN: an unticked row is not checked at all.
 *   * ONE EDITOR, not one per row: rows are rebuilt on every save. Read-only for a technician, since
 *     creating and editing templates is admin-only on the server.
 */
import type { CheckBox, TextBox } from 'webwin';
import { insets } from 'webwin';
import * as auth from '../../api/auth.js';
import { describeError } from '../../api/http.js';
import * as wp from '../../api/windowsPolicy.js';
import type { SaveWindowsPolicyRequest, WindowsPolicyDto, WindowsPolicySettings } from '../../contracts.gen.js';
import { join, localDate, plural } from '../../format.js';
import { Card } from '../../ui/Card.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  field,
  FONT,
  GAP,
  h2,
  inlineRow,
  muted,
  relayout,
  stackOf,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import { SETTING_GROUPS, SETTING_SPECS, emptySettings, settingValue, summarizeSettings, type SettingSpec } from '../../windowsPolicy.js';
import type { PageContext } from '../page.js';

export interface WindowsPoliciesSection {
  card: Card;
  load(): Promise<void>;
}

interface SettingRow {
  spec: SettingSpec;
  check: CheckBox;
  box: TextBox | null;
}

export function windowsPoliciesSection(ctx: PageContext, customerId: number, onAssigned: () => Promise<void>): WindowsPoliciesSection {
  const isAdmin = auth.isAdmin();
  let policies: WindowsPolicyDto[] = [];
  let editing: WindowsPolicyDto | null = null;

  const list = new CardList<WindowsPolicyDto>({
    key: p => p.id,
    version: p => `${p.updatedUtc}|${p.deviceCount}|${p.isDefault}`,
    build: buildPolicyCard
  });
  const newButton = button('New Windows policy', () => openEditor(null));
  newButton.visible = isAdmin;
  const listStatus = new StatusLine();

  // ---- Editor ----
  const editorTitle = h2('New Windows policy');
  const name = textBox({ placeholder: 'e.g. Standard, Front desk, Laptops' });
  const isDefault = wrappingCheck('Default for newly approved PCs');
  const rows: SettingRow[] = [];
  const groups = vstack({ spacing: GAP.md });
  for (const group of SETTING_GROUPS) {
    const panel = vstack({ spacing: GAP.sm });
    panel.controls.add(value(group, { font: FONT.bold }));
    for (const spec of SETTING_SPECS.filter(s => s.group === group)) {
      const check = wrappingCheck(spec.number ? `${spec.label} ... ${spec.number.unit}` : spec.label);
      let box: TextBox | null = null;
      panel.controls.add(checkLine(check));
      if (spec.number) {
        const numberBox = textBox({ placeholder: String(spec.number.suggested), enabled: false });
        box = numberBox;
        check.on('change', () => {
          numberBox.enabled = check.checked && isAdmin;
          if (check.checked && !numberBox.text.trim()) numberBox.text = String(spec.number!.suggested);
        });
        panel.controls.add(field(`${spec.number.unit} (${spec.number.min}-${spec.number.max})`, numberBox));
      }
      check.enabled = isAdmin;
      rows.push({ spec, check, box });
    }
    groups.controls.add(panel);
  }

  const editorStatus = new StatusLine();
  const saveButton = button('Save policy', () => void save(), true);
  saveButton.visible = isAdmin;
  const editor = card({ tint: 'alt', visible: false });
  editor.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      editorTitle,
      muted(
        isAdmin
          ? 'Tick what this customer\'s PCs must have. Each PC changes any setting that does not match, then reports. A PC already stricter than this is left alone.'
          : 'Only an administrator can change Windows policies.'
      ),
      field('Name', name),
      checkLine(isDefault),
      groups,
      stackOf(buttonRow(), saveButton, button(isAdmin ? 'Cancel' : 'Close', () => closeEditor())),
      editorStatus.label
    )
  );
  name.enabled = isAdmin;
  isDefault.enabled = isAdmin;

  const shell = card();
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('Windows policies'),
      muted("What this customer's Windows PCs must be set to. The agent changes any setting that does not match and reports what it found and did."),
      list.panel,
      stackOf(buttonRow(), newButton),
      listStatus.label,
      editor
    )
  );

  function buildPolicyCard(p: WindowsPolicyDto): Card {
    const left = vstack({ spacing: GAP.xs });
    const title = inlineRow();
    title.controls.add(value(p.name, { font: FONT.bold }));
    if (p.isDefault) title.controls.add(chip('default', 'brand'));
    left.controls.add(title);
    left.controls.add(muted(summarizeSettings(p.settings)));
    left.controls.add(
      muted(join([plural(p.deviceCount, 'PC'), `changed ${localDate(p.updatedUtc)}${p.updatedBy ? ` by ${p.updatedBy}` : ''}`], ' · '))
    );

    // Buttons go under the text: three of them beside it squeezed the summary and clipped the last one.
    const actions = buttonRow();
    actions.controls.add(button(isAdmin ? 'Edit' : 'View', () => openEditor(p)));
    actions.controls.add(button('Assign unassigned PCs', () => void assignUnassigned(p)));
    if (isAdmin) actions.controls.add(button('Delete', () => void remove(p)));
    left.controls.add(actions);

    const c = card({ padding: insets(12) });
    c.controls.add(left);
    return c;
  }

  function openEditor(p: WindowsPolicyDto | null): void {
    editing = p;
    editorTitle.text = p ? (isAdmin ? `Edit "${p.name}"` : p.name) : 'New Windows policy';
    name.text = p?.name ?? '';
    isDefault.checked = p?.isDefault ?? policies.length === 0;
    const settings: WindowsPolicySettings = p?.settings ?? emptySettings();
    for (const r of rows) {
      const v = settings[r.spec.key];
      r.check.checked = v !== null && v !== undefined && v !== false;
      if (r.box) {
        r.box.text = typeof v === 'number' ? String(v) : '';
        r.box.enabled = r.check.checked && isAdmin;
      }
    }
    editorStatus.clear();
    editor.visible = true;
    relayout(editor);
  }

  function closeEditor(): void {
    editing = null;
    editor.visible = false;
    relayout(shell);
  }

  function buildRequest(): SaveWindowsPolicyRequest | string {
    const policyName = name.text.trim();
    if (!policyName) return 'Give the policy a name.';
    const settings = emptySettings() as unknown as Record<string, boolean | number | null>;
    for (const r of rows) {
      const v = settingValue(r.spec, r.check.checked, r.box?.text ?? '');
      if (typeof v === 'string') return v;
      settings[r.spec.key] = v;
    }
    return { name: policyName, isDefault: isDefault.checked, settings: settings as unknown as WindowsPolicySettings };
  }

  async function save(): Promise<void> {
    const req = buildRequest();
    if (typeof req === 'string') {
      editorStatus.error(req);
      return;
    }
    saveButton.enabled = false;
    editorStatus.busy('Saving…');
    try {
      const result = editing ? await wp.updateWindowsPolicy(editing.id, req) : await wp.createWindowsPolicy(customerId, req);
      if (ctx.cancelled()) return;
      closeEditor();
      await refresh();
      listStatus.ok(
        `Saved "${result.policy.name}".` + (result.pushed > 0 ? ` ${plural(result.pushed, 'online PC')} will apply it now.` : '')
      );
    } catch (error) {
      if (!ctx.cancelled()) editorStatus.error('Could not save the policy: ' + describeError(error));
    } finally {
      saveButton.enabled = true;
    }
  }

  async function assignUnassigned(p: WindowsPolicyDto): Promise<void> {
    if (
      !(await confirm(ctx.desktop, {
        title: 'Assign unassigned PCs',
        message: `Put every one of this customer's Windows PCs that has no policy on "${p.name}"? PCs already on a policy keep theirs. Each PC changes its settings to match this policy as soon as it is online.`
      }))
    )
      return;
    listStatus.busy('Assigning…');
    try {
      const result = await wp.assignUnassignedPcs(p.id);
      if (ctx.cancelled()) return;
      await refresh();
      await onAssigned();
      listStatus.ok(result.assigned === 0 ? 'Every Windows PC already has a policy.' : `Assigned ${plural(result.assigned, 'PC')} to "${p.name}".`);
    } catch (error) {
      if (!ctx.cancelled()) listStatus.error(describeError(error));
    }
  }

  async function remove(p: WindowsPolicyDto): Promise<void> {
    if (!(await confirm(ctx.desktop, { title: 'Delete Windows policy', message: `Delete "${p.name}"? A policy still assigned to PCs cannot be deleted.` }))) return;
    try {
      await wp.deleteWindowsPolicy(p.id);
      if (ctx.cancelled()) return;
      if (editing?.id === p.id) closeEditor();
      await refresh();
      listStatus.ok(`Deleted "${p.name}".`);
    } catch (error) {
      if (!ctx.cancelled()) listStatus.error(describeError(error));
    }
  }

  async function refresh(): Promise<void> {
    policies = await wp.getWindowsPolicies(customerId, ctx.dispose.signal());
    if (ctx.cancelled()) return;
    list.set(policies);
    relayout(shell);
  }

  return {
    card: shell,
    async load() {
      try {
        await refresh();
      } catch (error) {
        if (!ctx.cancelled()) listStatus.error('Could not load the Windows policies: ' + describeError(error));
      }
    }
  };
}
